import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { AnswerReaction, ChatbotMessage, DocumentSource, MessageType, SourceType } from '@types';
import { Store } from '@ngrx/store';
import { AppState } from '../../state/app-state';
import { addMessage, messagesStateSelector, resetMessages, updateMessage } from './chatbot-state';
import { Observable, Subject, takeUntil } from 'rxjs';
import {
  ButtonComponent,
  FormFieldComponent,
  FormFieldModule,
  FormFieldSuffixDirective,
  IconButtonComponent,
  IconComponent,
  TagComponent,
  TileComponent,
  TypographyComponent
} from '@totalenergiescode/tds-angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { MarkdownModule } from 'ngx-markdown';
import { FormsModule } from '@angular/forms';
import { v4 as uuidV4 } from 'uuid';
import { ConfirmationModalComponent } from '../shared/confirmation-modal/confirmation-modal.component';
import { ChatService } from './chatbot.service';
import { AgGridModule, AgGridAngular } from 'ag-grid-angular';

type messageDate = {
  date: string;
  question: string;
};

@Component({
  selector: 'app-chatbot',
  imports: [
    CommonModule,
    TranslateModule,
    ButtonComponent,
    FormFieldComponent,
    FormFieldSuffixDirective,
    FormFieldModule,
    IconComponent,
    IconButtonComponent,
    MarkdownModule,
    FormsModule,
    TagComponent,
    TypographyComponent,
    ConfirmationModalComponent,
    NgOptimizedImage,
    TileComponent,
    AgGridModule
  ],
  providers: [ChatService],
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.scss']
})
export class ChatbotComponent implements OnInit, AfterViewInit, OnDestroy {
  messages: ChatbotMessage[] = [];
  messages$: Observable<ChatbotMessage[]>;

  public messageToSend: ChatbotMessage = {
    question: '',
    type: MessageType.QUESTION
  };
  displayedMessage: string = '';
  displayedResult: string = '';
  disableSendMsg = false;
  sourceActive?: SourceType = SourceType.HSE;
  robotThinking = false;
  index = 0;
  messageDates: messageDate[] = [];
  isPaused = false;
  timeoutId: ReturnType<typeof setTimeout> | undefined;
  ignoreMessage = false;
  currentMessageDocument: DocumentSource[] = [];
  currentQuestionId: string = '';
  confirmationModalContent = {
    title: '',
    message: '',
    buttonText: '',
    action: () => {}
  };
  showTable = false;
  tableRowData: any[] = [];
  tableColumnDefs: any[] = [];
  protected readonly SourceType = SourceType;
  private readonly destroy$ = new Subject<void>();
  @ViewChild('messagesContainer') private readonly messagesContainer!: ElementRef;
  @ViewChild('questionInput') private readonly questionInput!: ElementRef;
  @ViewChildren('agGrid') agGrids!: QueryList<AgGridAngular>;

  constructor(
    public chatService: ChatService,
    public translate: TranslateService,
    private readonly store: Store<AppState>,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {
    this.messages$ = this.store.select(messagesStateSelector);
  }

  ngOnInit(): void {
    this.messages$.pipe(takeUntil(this.destroy$)).subscribe((messages) => {
      this.messages = messages;
    });
  }

  handleDisplayMessage() {
    this.robotThinking = true;

    this.chatService.handleAnswer(this.messageToSend).subscribe((message: ChatbotMessage) => {
      this.revealMessage(message);

      this.robotThinking = false;
    });
  }

  ngAfterViewInit() {
    this.changeDetectorRef.detectChanges();
  }

  askQuickQuestion(value: string) {
    this.messageToSend.question = this.translate.instant(value);
  }

  public sendMessage() {
    if (
      (this.messageToSend.question && this.messageToSend.question.trim() === '') ||
      this.disableSendMsg ||
      this.sourceActive === undefined
    ) {
      return;
    }
    this.robotThinking = true;
    this.isPaused = false;
    const date = new Date();
    const formattedDate = date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    const formattedTime = date.toLocaleString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
    this.handleDisplayDateMessage(formattedDate);
    this.currentQuestionId = uuidV4();
    this.messageToSend = {
      question: this.messageToSend.question,
      questionId: this.currentQuestionId,
      date: formattedDate,
      type: MessageType.QUESTION,
      source: this.sourceActive,
      time: formattedTime
    };

    ///this.appInsightsService.logEvent(CHATBOT_ASK, { question: this.messageToSend?.question });

    this.handleDisplayMessage();
    this.store.dispatch(addMessage({ message: this.messageToSend }));
    this.messageToSend = { question: '', source: undefined, type: MessageType.QUESTION };
    this.scrollToBottom();
    this.questionInput.nativeElement.style.height = '1rem';
    this.questionInput.nativeElement.classList.remove('scroll');

    // Set a timeout for 2 minutes
    this.timeoutId = setTimeout(() => {
      if (this.robotThinking) {
        // If robot is still thinking after 2 minutes, ignore the message
        this.robotThinking = false;
        this.disableSendMsg = false;
        this.displayedMessage = '';
        this.currentMessageDocument = [];
        this.ignoreMessage = true;
      }
    }, 90_000);
  }

  revealMessage(messagePart: ChatbotMessage): void {
    clearTimeout(this.timeoutId);
    this.robotThinking = false;
    this.disableSendMsg = true;

    // Check status first
    if (messagePart.status && messagePart.status.status_code !== 200) {
      this.displayedMessage = messagePart.status.message || 'An error occurred.';
      this.storeMessage(messagePart);
      this.disableSendMsg = false;
      this.isPaused = true;
      return;
    }

    // ag-Grid table logic for new format: result is array of arrays, columns is array of objects with 'name'
    if (
      Array.isArray(messagePart.result) &&
      messagePart.result.every((row) => Array.isArray(row)) &&
      Array.isArray(messagePart.columns) &&
      messagePart.columns.every((col) => col && typeof col.name === 'string') &&
      messagePart.result.length > 0
    ) {
      // Convert columns to ag-Grid column definitions
      const colDefs = messagePart.columns.map((col) => ({ field: col.name }));
      // Map result rows to objects based on column definitions
      const rowData = messagePart.result.map((row) => {
        const obj: Record<string, any> = {};
        colDefs.forEach((col, idx) => {
          obj[col.field] = row[idx];
        });
        return obj;
      });
      // Attach table data to the message
      messagePart.tableColumnDefs = colDefs;
      messagePart.tableRowData = rowData;
      messagePart.showTable = true;
    } else {
      messagePart.showTable = false;
    }

    if (messagePart.query) {
      this.displayedMessage += `Query: ${messagePart.query}\n\n`;
    }
    if (messagePart.query) {
      this.storeMessage(messagePart);
    }

    if (messagePart.documents?.length) {
      this.currentMessageDocument = messagePart.documents;
    }
  }

  // Overload storeMessage to accept the messagePart
  storeMessage(messagePart?: ChatbotMessage) {
    const answer = {
      question: this.messageToSend.question,
      questionId: this.currentQuestionId,
      answer: this.displayedMessage,
      type: MessageType.ANSWER,
      documents: this.currentMessageDocument,
      // Attach table data if present
      ...(messagePart?.tableColumnDefs && { tableColumnDefs: messagePart.tableColumnDefs }),
      ...(messagePart?.tableRowData && { tableRowData: messagePart.tableRowData }),
      ...(messagePart?.showTable !== undefined && { showTable: messagePart.showTable })
    };
    this.store.dispatch(addMessage({ message: answer }));
    this.displayedMessage = '';
    this.disableSendMsg = false;
    this.isPaused = true;
    this.currentMessageDocument = [];
  }

  stopGeneration(): void {
    this.storeMessage();
  }

  changeSource(source: SourceType) {
    this.sourceActive = source;
  }

  showConfirmationModal() {
    this.confirmationModalContent = {
      title: this.translate.instant('CHATBOT.NEW_CONVERSATION_TITLE'),
      message: this.translate.instant('CHATBOT.NEW_CONVERSATION_MESSAGE'),
      buttonText: this.translate.instant('CHATBOT.NEW_CONVERSATION_BUTTON'),
      action: () => {
        this.newConversation();
      }
    };
  }

  newConversation() {
    this.store.dispatch(resetMessages());

    this.messageToSend = { question: '', type: MessageType.QUESTION };
    this.displayedMessage = '';
    this.disableSendMsg = false;
    this.robotThinking = false;
    this.sourceActive = SourceType.HSE;
    this.isPaused = false;
    this.currentQuestionId = '';
    this.ignoreMessage = false;
    this.currentMessageDocument = [];
    this.messageDates = [];
    this.index = 0;
    this.showTable= false
  }

  showNewConversationButton(): boolean {
    return this.messages.filter((message) => message.type === MessageType.ANSWER).length > 0;
  }

  autoGrow(event: Event) {
    const textarea = event.target as HTMLTextAreaElement;
    const currentRows = textarea.value.split('\n').length;

    if (currentRows <= 4) {
      textarea.style.height = `${currentRows}rem`;
    } else {
      textarea.style.height = '4rem';
      textarea.style.overflowY = 'auto'; // Enable scrolling
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  displayDate(msg: ChatbotMessage): boolean {
    if (msg.type === 'ANSWER') return false;
    return this.messageDates.some(
      (date) => date.question === msg.question && date.date === msg.date
    );
  }

  handleDisplayDateMessage(formattedDate: string) {
    if (this.messageDates.length === 0) {
      return;
    }
  }

  getSmartToyIconSrc(): string {
    const bodyElement = document.querySelector('body');
    return bodyElement?.classList?.contains('dark')
      ? 'assets/images/svg/smart-toy-dark.svg'
      : 'assets/images/svg/smart-toy.svg';
  }

  openAnswerSource(url: string) {}

  copyMessageToClipboard(answer?: string) {
    if (!answer) {
      return;
    }

    navigator.clipboard.writeText(answer.replace('<EOA>', ''))?.then(() => {});
  }

  rateAnswer(message: ChatbotMessage, rating: AnswerReaction) {
    if (!message?.answer) {
      return;
    }

    this.store.dispatch(updateMessage({ message: { ...message, answerReactions: rating } }));
  }

  exportTableToExcel(gridApi: any) {
    if (gridApi && gridApi.exportDataAsExcel) {
      gridApi.exportDataAsExcel({
        fileName: 'chatbot-table.xlsx',
        sheetName: 'Chatbot Data',
      });
    }
  }

  exportTableToExcelByIndex(index: number) {
    const grids = this.agGrids.toArray();
    const grid = grids[index];
    if (grid && grid.api && grid.api.exportDataAsExcel) {
      grid.api.exportDataAsExcel({
        fileName: 'chatbot-table.xlsx',
        sheetName: 'Chatbot Data',
      });
    }
  }

  private scrollToBottom() {
    try {
      this.messagesContainer.nativeElement.scrollTop =
        this.messagesContainer.nativeElement.scrollHeight;
    } catch (err) {
      console.error('Could not scroll to bottom', err);
    }
  }

  protected readonly AnswerReaction = AnswerReaction;
}
